<template>
  <div class="large-12">
    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h3>Stichtag</h3>
        <div v-if="!showStyle" class="spv-select-group flex-no-wrap row">
          <div
            v-for="d in deadlines"
            :key="d.id"
            :class="`small-6 medium-4 large-4 columns`"
          >
            <div class="radio-input">
              <input
                :id="`deka-${ d.id }`"
                :checked="deadline_id == d.id"
                :value="d.id"
                type="radio"
                name="deka-deadline"
                @change="onSelectDeadline(d.id)"
              >
              <label :class="{ 'checked': deadline_id == d.id }" :for="`deka-${ d.id }`">
                <p>{{ d.name }}</p>
              </label>
            </div>
          </div>
        </div>

        <div v-else class="spv-select-group">
          <div class="small-12 medium-6 large-4">
            <select :value="deadline_id" class="spv-input-select" @change="(e) => onSelectDeadline(e.target.value)">
              <option v-for="d in deadlines" :key="d.id" :value="d.id">
                {{ d.name }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-6 columns">
        <h3>Zeitraum</h3>
        <div class="large-8">
          <select :value="year_" class="spv-input-select" @change="(e) => onSelectYear(e.target.value)">
            <option v-for="y in years" :value="y" :key="y">
              {{
                deadline_id === 'j'
                  ? `Jun ${ y } - Mai ${ y + 1 }`
                  : `Dez ${ y } - Nov ${ y + 1 }`
              }}
            </option>
          </select>
        </div>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Normierte Wertentwicklung Deka-MultiAktiv Global {{ deadline_.name }}</h1>
        <div class="large-12 columns spv-chart-wrapper">
          <index-chart
            v-show="deadline_id === 'j'"
            :data="chart_items['j'] || []"
            :max="chart_max['j'] || 0"
            name="Deka-MultiAktiv Global"
            color="#E81700"
          />

          <index-chart
            v-show="deadline_id === 'd'"
            :data="chart_items['d'] || []"
            :max="chart_max['d'] || 0"
            name="Deka-MultiAktiv Global"
            color="#E81700"
          />

          <div class="spv-price-inquiry">
            <b class="spv-price-inquiry__label">Kursabfrage</b>

            <div class="spv-price-inquiry__date-picker">
              <span>Datum</span>
              <date-picker
                :min="min_date"
                :max="max_date"
                :value="inquiry_date"
                @select="onSelectInquiryDate"
              />
            </div>

            <div class="spv-price-inquiry__value">
              <span v-if="!inquiry_valid && !inquiry_date" class="error-value">
                Bitte wählen Sie ein Datum
              </span>
              <span v-else-if="inquiry_value === -1" class="error-value">
                Für dieses Datum liegt kein Datensatz vor. Bitte wählen Sie ein anderes Datum aus
              </span>
              <span v-else-if="!!inquiry_value">
                Deka-MultiAktiv Global&nbsp;&nbsp;<b>{{ inquiry_value }} Punkte</b>
              </span>
            </div>

            <a class="button button-small block" @click="loadDayValue">Anzeigen</a>
          </div>
        </div>
      </div>
    </div>

    <div v-if="configText" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <div v-html="configText" />
      </div>
    </div>

    <div v-if="show_data" class="row wrapper-spacing">
      <div v-if="details_data.index_values && details_data.index_values.length" class="medium-12 large-12 columns">
        <h1>
          <div class="spv-info-wrapper info-trend">
            Monatliche
            <div class="info-overlay">
              Wertentwicklung
              <div class="info-overlay__icon">
                <i class="fo icon-info_kreis" />
              </div>
              <div class="info-overlay__text" >
                {{ gloss_texts_trend[0] }}
              </div>
            </div>
          </div>
          <div class="spv-info-wrapper text-only">
            Deka-MultiAktiv Global {{ deadline_.name }}
          </div>
          <div class="spv-info-wrapper info-trend">
            <span class="text-normal">mit Zielvolatilität</span>
            <div class="info-overlay">
              <span class="text-normal">{{ targetVol }}</span>
              <div class="info-overlay__icon">
                <i class="fo icon-info_kreis" />
              </div>
              <div class="info-overlay__text" >
                {{ gloss_texts_trend[1] }}
              </div>
            </div>
          </div>
        </h1>

        <div class="spv-table-wrapper">
          <table class="spv-product-table spv-index-table">
            <thead>
              <tr>
                <th class="medium-8 large-9">
                  Indexstichtag<br>{{ deadline_.label }}{{ details_data.year }}
                </th>
                <th class="medium-4 large-3">
                  Indexstand<br>jeweils am<br>Ende des Monats
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(data, index) in details_data.index_values" :key="index">
                <td>{{ formatDate(data.date) }}</td>
                <td>{{ formatMoney(data.index_value, 2) }}</td>
              </tr>
            </tbody>

            <tfoot>
              <tr>
                <td class="td-padding">
                  <div class="spv-info-wrapper">
                    Jahresrendite
                    <div class="info-overlay">
                      <div class="info-overlay__icon">
                        <i class="fo icon-info_kreis" />
                      </div>
                      <div class="info-overlay__text right-auto" >
                        {{ gloss_texts_trend[3] }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{{ formatPercent(details_data.yearly_yield, 2) }}</td>
              </tr>

              <tr>
                <td>
                  <div class="spv-info-wrapper">
                    Maßgebliche
                    <div class="info-overlay">
                      Jahresrendite
                      <div class="info-overlay__icon">
                        <i class="fo icon-info_kreis" />
                      </div>
                      <div class="info-overlay__text right-auto" >
                        {{ gloss_texts_trend[4] }}
                      </div>
                    </div>
                  </div>
                </td>
                <td>{{ formatPercent(details_data.decisive_yearly_yield, 2) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="pdf-button-wrapper">
          <a class="button button-small" @click="onCreatePdf">
            Drucken
          </a>
        </div>
      </div>

      <div v-else class="medium-12 large-12 columns">
        <h1 class="no-margin">Keine Monatswerte für Index Deka-MultiAktiv Global für {{ deadline_.name }} vorhanden.</h1>
      </div>
    </div>

    <div class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <h1>Zusammensetzung der Anlageklassen Deka-MultiAktiv Global {{ deadline_.name }}</h1>
        <p><b>Stand {{ `${ assets_month_name } ${ assets_year }` }}</b></p>
        <p v-html="configTextOfAsset" />
      </div>

      <div class="medium-12 large-12 columns">
        <n-accordion-chart :items="chart_data" />
      </div>
    </div>

    <div v-if="factsheet_path && factsheet_path[deadline_id]" class="row wrapper-spacing">
      <div class="medium-12 large-12 columns">
        <div>
          <a class="button call-to-action" @click.stop="onOpenFactsheet(factsheet_path[`${ deadline_id }_path`])">
            Download Factsheet
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import mixin from './mixin';

import { b64DecodeUnicode } from '@/modules/utils';

export default {
  name: 'index_spv_deka',

  mixins:[
    mixin
  ],

  data: () => ({
    deadline_id: 'j',

    deadlines: [
      {
        id: 'j',
        name: 'Juni',
        label: '01.06.'
      },
      {
        id: 'd',
        name: 'Dezember',
        label: '01.12.',
      }
    ],

    index_config: null,
    config_values: null,
    factsheet_path: null,
    assets: [],
    assets_month: 0,
    assets_year: '',
    daily_data: [],
    show_data: false,

    details_data: [],
    chart_items: {},
    chart_max: {}
  }),

  computed: {
    assets_month_name () {
      if (!this.assets_month) {
        return '';
      }

      return this.month_names[this.assets_month - 1];
    },

    deadline_ () {
      return this.deadlines.find(d => d.id === this.deadline_id);
    },

    years () {
      if (!this.index_config) {
        return [];
      }

      return (this.index_config[this.deadline_id].years || [])
        .sort((a, b) => b - a);
    },

    max_date () {
      if (!this.index_config) {
        return moment().format('YYYY-MM-DD');
      }

      return this.index_config[this.deadline_id].min_max.max_date;
    },

    min_date () {
      if (!this.index_config) {
        return moment().format('YYYY-MM-DD');
      }

      return this.index_config[this.deadline_id].min_max.min_date;
    },

    chart_data () {
      const children2 = (a, a_) => this.assets
        .filter(a__ => a_.l1 === a.l1 && a__.l2 === a_.l2 && a__.l3)
        .sort((a1, a2) => a1.percent < a2.percent ? 1 : -1)
        .map(a__ => ({
          label: a__.l3,
          percent: a__.percent,
        }));

      const children1 = (a) => this.assets
        .filter(a_ => a_.l1 === a.l1 && a_.l2 && !a_.l3)
        .sort((a1, a2) => a1.percent < a2.percent ? 1 : -1)
        .map(a_ => ({
          label: a_.l2,
          percent: a_.percent,
          children: children2(a, a_)
        }));

      return this.assets
        .filter(a => !a.l2 && !a.l3)
        .sort((a1, a2) => a1.percent < a2.percent ? 1 : -1)
        .map(a => ({
          label: a.l1,
          percent: a.percent,
          children: children1(a)
        }));
    },

    configText() {
      if (!this.config_values) {
        return '';
      }

      return this.deadline_id === 'd'
        ? b64DecodeUnicode(this.config_values.deka_einleitung_dezember.value)
        : b64DecodeUnicode(this.config_values.deka_einleitung_juni.value);
    },

    configTextOfAsset() {
      if (!this.config_values) {
        return '';
      }

      return this.deadline_id === 'd'
        ? b64DecodeUnicode(this.config_values.deka_asset_dezember.value)
        : b64DecodeUnicode(this.config_values.deka_asset_juni.value);
    },

    targetVol() {
      return this.details_data && this.details_data.target_vol
        ? this.formatPercent(this.details_data.target_vol, 2)
        : '0,00%';
    }
  },

  methods: {
    loadIndexConfig () {
      if (!this.apiRequest || this.years.length > 0) {
        return;
      }

      this.apiRequest
        .get(this.config.paths.deka_years)
        .then(res => {
          this.config_values = res.data.config_values;
          this.index_config = res.data.index_data;
          this.show_data = res.data.show_data;
          this.assets = res.data.assets || [];
          this.assets_month = res.data.assets_month;
          this.assets_year = res.data.assets_year;
          this.factsheet_path = {
            j: res.data.factsheet_filename_jun,
            j_path: res.data.factsheet_path_jun,
            d: res.data.factsheet_filename_dec,
            d_path: res.data.factsheet_path_dec,
          };

          this.year_ = this.years[0];
          this.inquiry_date = this.max_date;

          this.deadlines.forEach(d => {
            this.loadDailyData(d.id);
          });

          this.loadDetails();
        });
    },

    loadDailyData (type) {
      if (!this.apiRequest || !!this.chart_items[this.deadline_id]) {
        return;
      }

      const params = {
        type
      };

      this.apiRequest
        .get(this.config.paths.deka_daily, {
          params
        })
        .then(res => {
          this.setChartData(type, res.data || []);
        });
    },

    loadDetails () {
      if (!this.show_data) {
        return;
      }

      let year = parseInt(this.year_);

      if (!year || isNaN(year)) {
        return;
      }

      const params = {
        type: this.deadline_id,
        effektive_year: year,
      };

      this.apiRequest
        .get(this.config.paths.deka_query, {
          params
        })
        .then(res => {
          this.details_data = res.data || {};
        });
    },

    loadDayValue() {
      if (!this.inquiry_date) {
        this.inquiry_value = '';
        this.inquiry_valid = false;
        return;
      }

      this.inquiry_valid = true;

      let params = {
        type: this.deadline_id,
        date: this.formatDate(this.inquiry_date, 'YYYY-MM-DD[T]HH:mm:ss.SSS[Z]')
      };

      this.apiRequest
        .get(this.config.paths.deka_day, {
          params
        })
        .then(res => {
          this.inquiry_value = res.data.index_value
            ? this.formatMoney(res.data.index_value, 2)
            : -1;
        })
        .catch(err => {
          console.error(err);
          this.inquiry_value = -1;
        });
    },

    onSelectDeadline (value) {
      this.deadline_id = value;
      this.inquiry_value = '';
      this.inquiry_valid = false;
      this.inquiry_date = this.max_date;

      this.loadDetails();
    },

    setChartData (type, data) {
      this.chart_max[type] = 0;

      this.chart_items[type] = data
        .sort((a, b) => a.date > b.date ? 1 : -1)
        .map(item => {
          let value = parseFloat(item.index_value);

          if (value > this.chart_max[type]) {
            this.chart_max[type] = value;
          }

          return [
            moment(item.date).valueOf(),
            value
          ];
        });

      this.chart_items = {
        ...this.chart_items
      };
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/apps/index/spv_deka.vue